import { FC } from 'react'
import { ReactComponent as AnnouncementIcon } from 'src/assets/svg/announcement.svg'
import { ReactComponent as ArrowUpIcon } from 'src/assets/svg/ri-arrow-up-circle-fill.svg'
import { ReactComponent as StarIcon } from 'src/assets/svg/ri-star-fill.svg'
import { ReactComponent as DiagonalArrowUp } from 'src/assets/svg/diagonalArrowUp.svg'
import { ReactComponent as DiagonalArrowDown } from 'src/assets/svg/diagonalArrowDown.svg'
import styles from './campaignComparisonChart.module.scss'

interface CampaignData {
  id: string
  mentions: number
  name: string
  sentiments?: {
    negative: number
    neutral: number
    positive: number
  }
  sources?: Record<string, number>
}

interface SourcesTabProps {
  benchmarkCampaign: string | null
  selectedData: CampaignData[]
  stats?: any
}

const sourceColors: Record<string, string> = {
  booking: '#A855F7',
  facebook: '#FF0000',
  google: '#FBBF24',
  googleReviews: '#3B82F6',
  instagram: '#A78BFA',
  linkedin: '#F472B6',
  newsBlogs: '#34D399',
  reddit: '#6366F1',
  tiktok: '#F87171',
  tripadvisor: '#10B981',
  twitter: '#60A5FA',
  websites: '#9333EA',
  yandex: '#EC4899',
  youtube: '#FB923C',
}

export const SourcesTab: FC<SourcesTabProps> = ({
  selectedData,
  benchmarkCampaign,
  stats,
}) => {
  // Limit to max 9 campaigns
  const limitedData = selectedData.slice(0, 9)

  // Collect all unique sources from selected campaigns
  const allSourcesSet = new Set<string>()
  limitedData.forEach((campaign) => {
    if (campaign.sources) {
      Object.keys(campaign.sources).forEach((source) => {
        if (campaign.sources![source] > 0) {
          allSourcesSet.add(source)
        }
      })
    }
  })

  // Create legend items only for sources present in data
  const sourceNameMap: Record<string, string> = {
    booking: 'Booking',
    facebook: 'Facebook',
    google: 'Google',
    googleReviews: 'Google Reviews',
    instagram: 'Instagram',
    linkedin: 'LinkedIn',
    newsBlogs: 'News&Blogs',
    reddit: 'Reddit',
    tiktok: 'TikTok',
    tripadvisor: 'Tripadvisor',
    twitter: 'X (Twitter)',
    websites: 'Websites',
    yandex: 'Yandex',
    youtube: 'Youtube',
  }

  const legendItems = Array.from(allSourcesSet)
    .sort()
    .map((source) => ({
      color: sourceColors[source],
      name: sourceNameMap[source] || source,
      source,
    }))
    .filter((item) => item.color) // Only include sources with defined colors
  // Use API stats if available
  const totalMentions =
    stats?.totalMentions ??
    limitedData.reduce((sum, campaign) => sum + campaign.mentions, 0)

  // Format source names with proper capitalization
  const formatSourceName = (sourceName: string): string => {
    if (!sourceName) return 'No data'
    return (
      sourceNameMap[sourceName] ||
      sourceName.charAt(0).toUpperCase() + sourceName.slice(1)
    )
  }

  // Calculate most/least popular sources if not provided by API
  let mostPopularSourceName = stats?.mostPopularSource?.name
  let leastPopularSourceName = stats?.leastPopularSource?.name

  if (!mostPopularSourceName || !leastPopularSourceName) {
    const sourceTotals: Record<string, number> = {}

    limitedData.forEach((campaign) => {
      if (campaign.sources) {
        Object.entries(campaign.sources).forEach(([source, count]) => {
          sourceTotals[source] = (sourceTotals[source] || 0) + count
        })
      }
    })

    const sourceEntries = Object.entries(sourceTotals).filter(
      ([, count]) => count > 0
    )

    if (sourceEntries.length > 0) {
      if (!mostPopularSourceName) {
        const [source] = sourceEntries.reduce((max, entry) =>
          entry[1] > max[1] ? entry : max
        )
        mostPopularSourceName = source
      }
      if (!leastPopularSourceName) {
        const [source] = sourceEntries.reduce((min, entry) =>
          entry[1] < min[1] ? entry : min
        )
        leastPopularSourceName = source
      }
    }
  }

  const mostPopularSource = formatSourceName(mostPopularSourceName || '')
  const leastPopularSource = formatSourceName(leastPopularSourceName || '')

  // Calculate max value for chart scaling
  const maxMentions = Math.max(...limitedData.map((c) => c.mentions), 10)
  const chartMax = Math.ceil(maxMentions * 1.2) // Add 20% padding

  // Generate Y-axis labels
  const yAxisLabels = []
  const step = Math.ceil(chartMax / 5)
  for (let i = 5; i >= 0; i--) {
    yAxisLabels.push(step * i)
  }

  return (
    <>
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <AnnouncementIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>
              {/* Only the compared campaigns, unlike the page's own Total
                  Mentions above — say so, or the two read as a contradiction. */}
              Mentions · {limitedData.length} selected
              {limitedData.length === 1 ? ' campaign' : ' campaigns'}
            </div>
            <div className={styles.statValue}>
              {totalMentions.toLocaleString()}
            </div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Most Popular Source</div>
            <div className={styles.statValue}>{mostPopularSource}</div>
          </div>
        </div>

        <div className={styles.statCard}>
          <div className={styles.statIcon}>
            <ArrowUpIcon style={{ transform: 'rotate(180deg)' }} />
          </div>
          <div className={styles.statContent}>
            <div className={styles.statLabel}>Least Popular Source</div>
            <div className={styles.statValue}>{leastPopularSource}</div>
          </div>
        </div>
      </div>

      <div className={styles.chartContainer}>
        <div className={styles.chart}>
          <div className={styles.yAxis}>
            {yAxisLabels.slice(0, 5).map((label) => (
              <div className={styles.yLabel} key={label}>
                {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
              </div>
            ))}
          </div>

          <div className={styles.chartArea}>
            <div className={styles.gridLines}>
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
              <div className={styles.gridLine} />
            </div>

            <div className={styles.bars}>
              {limitedData.map((campaign) => {
                const campaignSources = campaign.sources || {}
                const totalMentions = campaign.mentions || 0

                // Skip rendering if no data
                if (totalMentions === 0) {
                  return (
                    <div className={styles.barWrapper} key={campaign.id}>
                      <div
                        className={styles.stackedBar}
                        style={{ height: '0px' }}
                      />
                    </div>
                  )
                }

                const sortedSources = Object.entries(campaignSources).sort(
                  ([, a], [, b]) => (b as number) - (a as number)
                )

                // Calculate the actual bar height in pixels (max 260px)
                const maxBarHeight = 260
                const barHeightPixels =
                  chartMax > 0 ? (totalMentions / chartMax) * maxBarHeight : 0

                // Ensure minimum height for visibility
                const actualBarHeight =
                  totalMentions > 0 ? Math.max(barHeightPixels, 20) : 0

                const isBenchmark = campaign.id === benchmarkCampaign

                // Calculate percentage difference from benchmark
                let percentageDiff = null
                if (benchmarkCampaign && !isBenchmark) {
                  const benchmarkData = limitedData.find(
                    (c) => c.id === benchmarkCampaign
                  )
                  if (benchmarkData && benchmarkData.mentions > 0) {
                    percentageDiff =
                      ((totalMentions - benchmarkData.mentions) /
                        benchmarkData.mentions) *
                      100
                  }
                }

                return (
                  <div className={styles.barWrapper} key={campaign.id}>
                    <div
                      className={styles.stackedBar}
                      style={{ height: `${actualBarHeight}px` }}
                    >
                      {sortedSources.map(([source, value]) => {
                        // Calculate segment as percentage of total mentions
                        const segmentPercentage =
                          totalMentions > 0
                            ? ((value as number) / totalMentions) * 100
                            : 0

                        return (
                          <div
                            className={styles.barSegment}
                            key={source}
                            style={{
                              backgroundColor: sourceColors[source],
                              height: `${segmentPercentage}%`,
                            }}
                          />
                        )
                      })}
                    </div>
                    <div className={styles.barTooltip}>
                      <div className={styles.tooltipTitle}>{campaign.name}</div>
                      {sortedSources.slice(0, 5).map(([source, value]) => (
                        <div className={styles.tooltipRow} key={source}>
                          <span className={styles.tooltipLabel}>
                            {`${source.charAt(0).toUpperCase()}${source.slice(1)}:`}
                          </span>
                          <span className={styles.tooltipValue}>
                            {(value as number).toLocaleString()}
                          </span>
                        </div>
                      ))}
                      {sortedSources.length > 5 && (
                        <div className={styles.tooltipRow}>
                          <span className={styles.tooltipLabel}>...</span>
                        </div>
                      )}
                      {percentageDiff !== null && (
                        <div
                          className={`${styles.tooltipDiff} ${
                            percentageDiff >= 0
                              ? styles.positive
                              : styles.negative
                          }`}
                        >
                          {percentageDiff >= 0 ? (
                            <DiagonalArrowUp className={styles.tooltipArrow} />
                          ) : (
                            <DiagonalArrowDown
                              className={styles.tooltipArrow}
                            />
                          )}
                          {Math.abs(percentageDiff).toFixed(1)}%
                        </div>
                      )}
                      {isBenchmark && (
                        <div className={styles.tooltipBenchmark}>Benchmark</div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className={styles.xAxis}>
              {limitedData.map((campaign) => {
                const isBenchmark = campaign.id === benchmarkCampaign
                return (
                  <div
                    className={styles.xLabel}
                    key={campaign.id}
                    onMouseEnter={(e) => {
                      const nameElement = e.currentTarget.querySelector(
                        `.${styles.campaignName}`
                      ) as HTMLElement
                      const tooltipElement = e.currentTarget.querySelector(
                        `.${styles.campaignNameTooltip}`
                      ) as HTMLElement
                      if (
                        nameElement &&
                        tooltipElement &&
                        nameElement.scrollWidth > nameElement.clientWidth
                      ) {
                        tooltipElement.style.opacity = '1'
                        tooltipElement.style.visibility = 'visible'
                      }
                    }}
                    onMouseLeave={(e) => {
                      const tooltipElement = e.currentTarget.querySelector(
                        `.${styles.campaignNameTooltip}`
                      ) as HTMLElement
                      if (tooltipElement) {
                        tooltipElement.style.opacity = '0'
                        tooltipElement.style.visibility = 'hidden'
                      }
                    }}
                  >
                    <span className={styles.campaignName}>
                      {campaign.name}
                      {isBenchmark && (
                        <StarIcon className={styles.benchmarkIcon} />
                      )}
                    </span>
                    <div className={styles.campaignNameTooltip}>
                      {campaign.name}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className={styles.yAxisRight}>
            {yAxisLabels.slice(0, 5).map((label) => (
              <div className={styles.yLabel} key={label}>
                {label > 999 ? `${(label / 1000).toFixed(1)}k` : label}
              </div>
            ))}
          </div>
        </div>

        <div className={styles.legendContainer}>
          {legendItems.map(({ color, name }) => (
            <div className={styles.legendItem} key={name}>
              <div
                className={styles.legendColor}
                style={{ backgroundColor: color }}
              />
              <span>{name}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
